import { between } from 'polished';
/**
 * List of degrees with standard intervals
 * @type {array<string>}
 */

var degrees = ['0.001deg', '45deg', '90deg', '135deg', '180deg', '225deg', '270deg', '315deg', '360deg'];
/**
 * Returns a CSS clamp(calc()) formula for linear interpolation of a property between two values
 *
 * @param  {string|number} min Minimum value
 * @param  {string|number} max Maximum value
 * @param  {?array<string>} [from, to]=['375px', '2500px'] Fluid range, linear interpolation will occur in this range
 * @returns {string} 'clamp(45px,calc(21.18px + 6.35vw),180px)'
 */

var fluidRange = function fluidRange(min, max, _temp) {
  var _ref = _temp === void 0 ? ['375px', '2500px'] : _temp,
      from = _ref[0],
      to = _ref[1];

  return "clamp(" + min + ", " + between(min, max, from, to) + ", " + max + ")";
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
} // List of default size keys


var __sizes = ['x', 's', 'm', 'l', 'xl', 'xxl', 'xxxl']; // Filters instances

var __shouldUse = function __shouldUse(entries) {
  if (Array.isArray(entries)) {
    return entries.filter(function (_ref) {
      var _ref$ = _ref[3],
          use = _ref$ === void 0 ? true : _ref$;
      return use;
    });
  }

  return Object.values(entries).filter(function (value) {
    return value.length === 2 || value[2] === true;
  });
};
/**
 * Generate a CSS Custom Property with a clamp() value
 *
 * @param  {[string, array<string>]} [key, values] Name and value for the property
 * @param  {number} i Index of the proprties group, assign a size key
 *
 * @example toStringClamp(['--stroke', ['2px', '0.2vw', '3.5px']], 0)
 * @returns {object} '--stroke-x': 'clamp(10px, 2vw, 60px)'
 */


var toStringClamp = function toStringClamp(_ref, i) {
  var _ref2;

  var key = _ref[0],
      values = _ref[1];
  return _ref2 = {}, _ref2[key + "-" + __sizes[i]] = "clamp(" + values + ")", _ref2;
};
/**
 * Create a scale of "clamp()" values & print CSS custom properties
 *
 * @param  {array} entries=[] [min<string>, value<string>, max<string>, regiser<boolean>]
 * @param  {string} name='' Common name for the propertie group
 *
 * @example makeClamps([
 *   ['2px', '0.2vw', '3.5px'],
 *   ['2.5px', '0.3vw', '4px'],
 * ], '--stroke')
 *
 * @returns {object}
 * scale [0, 'clamp(1.5px,0.15vw,2.45px)', 'clamp(2px,0.2vw,3.5px)'],
 * properties: {
 *  '--stroke-x': 'clamp(1.5px,0.15vw,2.45px)',
 *  '--stroke-s': 'clamp(2px,0.2vw,3.5px)',
 * }
 */


var makeClamps = function makeClamps(entries, name) {
  if (entries === void 0) {
    entries = [];
  }

  if (name === void 0) {
    name = '';
  }

  var scale = [0].concat(__shouldUse(entries).map(function (value) {
    return "clamp(" + value + ")";
  }));
  var properties = entries.reduce(function (acc, obj, i) {
    return _extends({}, acc, toStringClamp([name, obj.slice(0, 3)], i));
  }, {});
  return {
    scale: scale,
    properties: properties
  };
};
/**
 * Generate a CSS Custom Property with a clamp(between()) value
 *
 * @param  {string} name Name of the custom property
 * @param  {string|number} min Minimum value
 * @param  {string|number} max Maximum value
 * @param  {?array<string>} [from, to]=['375px', '2500px'] Fluid range, linear interpolation will occur in this range
 *
 * @example toStringBetween(['--padding', '10px', '60px'])
 * @returns {object} '--padding': 'clamp(15px,calc(7.06px + 2.12vw),60px)'
 */


var toStringBetween = function toStringBetween(_ref, _ref2) {
  var _ref3;

  var name = _ref[0],
      min = _ref[1],
      max = _ref[2];
  var from = _ref2[0],
      to = _ref2[1];
  return _ref3 = {}, _ref3[name] = fluidRange(min, max, [from, to]), _ref3;
};
/**
 * Create a scale of fluid values & print CSS custom properties
 *
 * @param  {array} entries=[] [min<string>, value<string>, max<string>, regiser<boolean>]
 * @param  {string} name='' Common name for the propertie group
 *
 * @example makeFluids({
 *   'padding-x': ['15px', '60px'],
 *   'padding-s': ['24px', '68px'],
 *   'skip': ['24px', '68px', false],
 * }, ['375px', '2500px'])
 *
 * @returns {object}
 * scale [0, 'clamp(15px,calc(7.06px + 2.12vw),60px)', 'clamp(24px,calc(16.24px + 2.07vw),68px)'],
 * properties: {
 *  '--stroke-x': 'clamp(15px,calc(7.06px + 2.12vw),60px)',
 *  '--stroke-s': 'clamp(24px,calc(16.24px + 2.07vw),68px)',
 * }
 */


var makeFluids = function makeFluids(entries, range) {
  if (entries === void 0) {
    entries = [];
  }

  if (range === void 0) {
    range = ['375px', '2500px'];
  }

  var scale = [0].concat(__shouldUse(entries).map(function (_ref4) {
    var min = _ref4[0],
        max = _ref4[1];
    return fluidRange(min, max, range);
  }));
  var properties = Object.entries(entries).reduce(function (acc, _ref5) {
    var key = _ref5[0],
        value = _ref5[1];
    return _extends({}, acc, toStringBetween(["--" + key].concat(value), range));
  }, {});
  return {
    scale: scale,
    properties: properties
  };
};

export { degrees, fluidRange, makeClamps, makeFluids };